import type { Vector2 } from '@sorrem/shared';
import type { UIMiniMapState } from '@sorrem-client/types';
import { useGameplay } from '@sorrem-client/ui/features/PlayPage/gameplay-context';
import { isShallowEqual, useGameMap, useGameSelector } from '@sorrem-client/ui/hooks';
import { useMemo } from 'react';
import { resolveKnownFloor, isFloorTile, resolveTileEdges, resolveTileOutline, type TileEdges } from './mini-map-floor';

/** Everything the mini map draws. Positions are in tile units. */
export interface MiniMapView extends Omit<UIMiniMapState, 'knownAreaIds'> {
    /** The known floor's edge as closed loops of tile corners, for its fill; holes wind the other way. */
    readonly floor: readonly (readonly Vector2[])[];
    /** The same edge as segments to stroke: walls, and openings where floor carries on (rooms not yet known). */
    readonly edges: TileEdges;
    /** The center of the local player's tile. */
    readonly self: Vector2;
}

/** The running game's mini map: the areas the local player knows, and the markers in them. */
export function useMiniMap(): MiniMapView {
    const { bridge } = useGameplay();
    const map = useGameMap(bridge);
    const miniMap = useGameSelector(bridge, (state) => state.miniMap);
    const playerMapPos = useGameSelector(bridge, (state) => state.playerMapPos, isShallowEqual);

    const { floor, edges } = useMemo(() => {
        if (!map) {
            return { floor: [], edges: { walls: [], openings: [] } };
        }
        const tiles = resolveKnownFloor(map.data, new Set(miniMap.knownAreaIds));
        const width = map.data.ground.width;
        return {
            floor: resolveTileOutline(tiles, width),
            edges: resolveTileEdges(tiles, (x, y) => isFloorTile(map.data, { x, y }), width),
        };
    }, [map, miniMap.knownAreaIds]);

    return {
        floor,
        edges,
        self: { x: playerMapPos.col + 0.5, y: playerMapPos.row + 0.5 },
        allies: miniMap.allies,
        enemies: miniMap.enemies,
        heart: miniMap.heart,
        essenceOrbs: miniMap.essenceOrbs,
        selfFacing: miniMap.selfFacing,
    };
}
